import { Text, useTheme } from '@querodelivery/uni-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseyeArrow,
  faChartMixed,
  faChevronLeft,
  faComment,
  faGift,
  faStar,
} from '@fortawesome/pro-solid-svg-icons';
import mockupCelular from '@/Assets/Img/mockup_celular.png';
import DeliverymanWithMoto from '@/Assets/svg/deliveryman-with-moto.svg';
import type { CreateMissionFormType } from '../../types';
import { buildMissionPreviewData } from '../../utils/build-mission-preview-data';
import styles from './mission-mobile-preview.module.scss';

type MissionMobilePreviewProps = {
  formValues: Pick<
    CreateMissionFormType,
    | 'missionName'
    | 'missionTitle'
    | 'missionDescription'
    | 'objective01'
    | 'objective02'
    | 'objective03'
    | 'reward01'
    | 'reward02'
    | 'reward03'
    | 'startDate'
    | 'startTime'
    | 'endDate'
    | 'endTime'
  >;
};

export const MissionMobilePreview = ({
  formValues,
}: MissionMobilePreviewProps) => {
  const { color } = useTheme();
  const preview = buildMissionPreviewData(formValues);

  const rewardLevelsInverted =
    preview && preview.rewardLevels.length > 0
      ? preview.rewardLevels.reverse()
      : [];

  return (
    <aside className={styles.phone} aria-hidden>
      <img
        src={mockupCelular}
        alt=""
        className={styles.frame}
        draggable={false}
      />
      <div className={styles.screen}>
        <div className={styles.statusBar} />
        <div className={styles.header}>
          <FontAwesomeIcon
            icon={faChevronLeft}
            color={color['color-action-on-fill']}
          />
          <Text
            style={{
              color: color['color-action-on-fill'],
            }}
            fontWeight="semibold"
            variant="headingMd"
          >
            Missão
          </Text>
          <FontAwesomeIcon
            icon={faComment}
            color={color['color-action-on-fill']}
          />
        </div>

        <div className={styles.content}>
          <div className={styles.contentBody}>
            <div className={styles.titleBox}>
              <FontAwesomeIcon
                icon={faBullseyeArrow}
                color={color['color-action-fill']}
                size="xl"
              />
              <div className={styles.titleContainer}>
                <Text tone="highlight" fontWeight="bold" variant="headingXs">
                  {preview.title}
                </Text>
                <Text tone="secondary" fontWeight="medium" variant="bodyXs">
                  {preview.description}
                </Text>
              </div>
              <img
                src={DeliverymanWithMoto}
                alt=""
                className={styles.motoboyIcon}
                draggable={false}
              />
            </div>

            <div className={styles.goalBoxContainer}>
              <div className={styles.goalBoxIconCircle}>
                <FontAwesomeIcon
                  icon={faChartMixed}
                  color={color['color-action-on-fill']}
                />
              </div>
              <div className={styles.goalBoxTextColumn}>
                <Text
                  tone="highlight"
                  variant="headingSm"
                  fontWeight="bold"
                  className={styles.goalBoxTitle}
                >
                  {preview.missionName}
                </Text>
                <div className={styles.goalBoxColumns}>
                  <div className={styles.goalBoxCol}>
                    <Text tone="supporting" fontWeight="medium" fontSize="xxs">
                      Meta máxima
                    </Text>
                    <Text tone="base" fontWeight="semibold" fontSize="xs">
                      {preview.maxValueLabel}
                    </Text>
                  </div>
                  <div className={styles.goalBoxColRight}>
                    <Text tone="supporting" fontWeight="medium" fontSize="xxs">
                      Média sugerida
                    </Text>
                    <Text tone="base" fontWeight="semibold" fontSize="xs">
                      {preview.suggestedAverageLabel}
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.rewardLevelsContainer}>
              <div className={styles.rewardLevelsHeader}>
                <Text tone="base" fontWeight="bold" variant="headingXs">
                  Níveis de recompensa
                </Text>
                <Text tone="supporting" fontWeight="medium" variant="bodyXs">
                  Atingiu a meta? Você ganha o bônus!
                </Text>
              </div>

              {rewardLevelsInverted.length > 0 && (
                <div className={styles.rewardLevelItemContainer}>
                  {rewardLevelsInverted.map(level => (
                    <div
                      key={level.position}
                      className={styles.rewardLevelItem}
                    >
                      <div className={styles.rewardLevelIconCircle}>
                        <FontAwesomeIcon
                          icon={faGift}
                          color={color['color-action-fill']}
                        />
                      </div>
                      <div className={styles.rewardLevelTextColumn}>
                        <Text tone="base" fontWeight="medium" variant="bodyXs">
                          {level.conditionText}
                        </Text>
                      </div>
                      <div className={styles.rewardLevelValueColumn}>
                        <Text
                          tone="highlight"
                          fontWeight="bold"
                          variant="headingSm"
                          className={styles.rewardLevelValue}
                        >
                          {level.rewardLabel}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.ruleBoxContainer}>
                <div className={styles.ruleBoxIconCircle}>
                  <FontAwesomeIcon icon={faStar} color="#febf00" />
                </div>
                <div className={styles.ruleBoxTextColumn}>
                  <Text tone="base" fontWeight="bold" variant="headingXs">
                    Regra importante
                  </Text>
                  <Text tone="base" fontWeight="medium" variant="bodyXs">
                    Você recebe o valor do{' '}
                    <Text
                      as="span"
                      tone="highlight"
                      fontWeight="bold"
                      variant="bodyXs"
                    >
                      maior nível
                    </Text>{' '}
                    que atingir durante o período da missão
                  </Text>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.contentSpacer} />

          <div className={styles.understandButton}>
            <Text
              style={{ color: color['color-action-on-fill'] }}
              fontWeight="semibold"
              variant="bodyMd"
            >
              entendi
            </Text>
          </div>
        </div>
      </div>
    </aside>
  );
};
